import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'
import VuePaginate from 'vue-paginate'

import store from '@/state/store'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})
Vue.use(VuePaginate)
const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  if (process.env.VUE_APP_DEFAULT_AUTH === "firebase") {
    // El sitio tiene dos zonas: el panel de administración (`authRequired`,
    // exige estar en `empleados`) y el portal de clientes (`clienteRequired`,
    // exige estar en `app_usuarios`). Ambas comparten el mismo Firebase Auth.
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired)
    const clienteRequired = routeTo.matched.some((route) => route.meta.clienteRequired)

    // If auth isn't required for the route, just continue.
    if (!authRequired && !clienteRequired) return next()

    if (store.getters['auth/loggedIn']) {
      // Validate the local user token...
      return store.dispatch('auth/validate').then((validUser) => {
        if (!validUser) return redirectToLogin()
        return clienteRequired ? resolverCliente() : resolverEmpleado()
      })
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin()

    // eslint-disable-next-line no-inner-declarations
    function resolverEmpleado() {
      // Tener sesión de Firebase no basta: la app móvil comparte el mismo
      // proyecto, así que se revalida la pertenencia a `empleados` en cada
      // navegación (con caché, no es una lectura por ruta).
      return store.dispatch('empleado/cargar').then((empleado) => {
        if (empleado) {
          if (routeTo.meta.soloAdmin && !store.getters['empleado/esAdmin']) {
            return next({ name: 'default' })
          }
          return next()
        }

        // No es empleado, pero puede ser un cliente del portal que llegó al
        // panel por error. Cerrarle la sesión sería expulsarlo del sitio: se le
        // manda a su zona y sólo se cierra sesión si tampoco es cliente.
        return store.dispatch('cliente/cargar').then((cliente) => {
          if (cliente) return next({ name: 'portal' })
          return store.dispatch('auth/logOut').then(redirectToLogin, redirectToLogin)
        })
      })
    }

    // eslint-disable-next-line no-inner-declarations
    function resolverCliente() {
      return store.dispatch('cliente/cargar').then((cliente) => {
        if (cliente) return next()
        // Credenciales válidas de Firebase pero sin perfil en la app: se le
        // explica en el login del portal en lugar de dejarlo en blanco.
        return next({
          name: 'portal-login',
          query: { sinPerfil: '1', redirectFrom: routeTo.fullPath },
        })
      })
    }

    // eslint-disable-next-line no-inner-declarations
    function redirectToLogin() {
      // Pass the original route to the login component
      const destino = clienteRequired ? 'portal-login' : 'login'
      next({ name: destino, query: { redirectFrom: routeTo.fullPath } })
    }
  } else {
    const publicPages = ['/login', '/register', '/forgot-password'];
    const authpage = !publicPages.includes(routeTo.path);
    const loggeduser = localStorage.getItem('user');

    if (authpage && !loggeduser) {
      return next('/login');
    }

    next();
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

export default router
